<script setup lang="ts">
import { ref } from 'vue';
import type { MapProvider } from '@gpx-spa/geo-domain';
import ShipmentFormRoutingSection from '@/components/shipment/form/map/ShipmentFormRoutingSection.vue';
import ShipmentFormAlternativesSection from '@/components/shipment/form/map/ShipmentFormAlternativesSection.vue';
import ShipmentFormMapPanel from '@/components/shipment/form/map/ShipmentFormMapPanel.vue';
import ShipmentFormAutofillSettingsPanel from '@/components/shipment/form/tracker/ShipmentFormAutofillSettingsPanel.vue';
import ShipmentFormLearnedDedupSettingsPanel from '@/components/shipment/form/tracker/ShipmentFormLearnedDedupSettingsPanel.vue';
import type { RoutingStrategy } from '@/components/shipment/form/utils/shipment-form-routing';

defineProps<{
  routingStrategy: RoutingStrategy;
  routeError: string | null;
  routeWarning?: string | null;
  mapProvider: MapProvider;
  showMapBusyOverlay: boolean;
  pathIds: string[];
  pathGroups?: Array<{ title: string; pathIds: string[] }>;
  pathUi: Record<
    string,
    {
      color: string;
      label: string;
      providerLabel?: string;
      segmentTitle?: string;
    }
  >;
  selectedByPathId: Record<string, boolean>;
  routingLoading: boolean;
  showAlternativesSection: boolean;
  showAlternativesHeaderSpinner: boolean;
  corridorBufferDebounceMs: number;
  commitCorridorBufferImmediately: () => void;
}>();

const emit = defineEmits<{
  mapReady: [];
  togglePath: [pathId: string, checked: boolean];
}>();

const corridorBufferM = defineModel<number>('corridorBufferM', {
  required: true,
});

const alternativesSectionRef = ref<{
  focusFirstRouteControl?: () => boolean;
} | null>(null);

function focusFirstRouteControl(): boolean {
  return alternativesSectionRef.value?.focusFirstRouteControl?.() ?? false;
}

defineExpose({
  focusFirstRouteControl,
});
</script>

<template>
  <div v-if="routingStrategy !== 'none'" class="relative z-0 mt-6">
    <section class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h2 class="text-sm font-semibold text-gpx_black-200">Route preview</h2>
      <ShipmentFormRoutingSection
        embedded
        class="mt-4"
        :route-error="routeError"
        :route-warning="routeWarning"
      />

      <ShipmentFormMapPanel
        embedded
        class="mt-4"
        :map-provider="mapProvider"
        :show-map-busy-overlay="showMapBusyOverlay"
        @ready="emit('mapReady')"
      />

      <p
        v-if="pathIds.length > 0"
        class="mt-4 text-xs font-medium text-gpx_gray-100"
      >
        {{ pathIds.length }} route{{ pathIds.length === 1 ? '' : 's' }} found
      </p>

      <ShipmentFormAlternativesSection
        v-if="showAlternativesSection"
        ref="alternativesSectionRef"
        v-model:corridor-buffer-m="corridorBufferM"
        embedded
        class="mt-2"
        :show-header-spinner="showAlternativesHeaderSpinner"
        :path-ids="pathIds"
        :path-groups="pathGroups"
        :path-ui="pathUi"
        :selected-by-path-id="selectedByPathId"
        :routing-loading="routingLoading"
        :corridor-buffer-debounce-ms="corridorBufferDebounceMs"
        :commit-corridor-buffer-immediately="commitCorridorBufferImmediately"
        @toggle-path="(pathId, checked) => emit('togglePath', pathId, checked)"
      />
    </section>

    <ShipmentFormLearnedDedupSettingsPanel />
    <ShipmentFormAutofillSettingsPanel />
  </div>
</template>
